import axios from 'axios';

import {
  Associate,
  AssociateFilter,
  AssociateSettingsBackendResponse,
  HeroConversation,
  ConversationContactResponse,
  Product,
  SavedReply,
  StoreOpeningHours,
  VsAssociateEvent,
  VsTransferEvent,
  ShopperEvent,
  ArchivedHeroConversationAttributes,
  AssociateSetupResponse,
  Store,
  IntroModule,
  AssociateInsight,
  Post,
  IntroModuleAsPost,
  SmartResponse,
  currencies,
} from '../../model';
import { store } from '../../redux';

export const httpClient = axios.create({
  baseURL: process.env.NX_PUBLIC_API_GATEWAY_ENDPOINT,
});

httpClient.interceptors.request.use(async (config) => {
  const { session: { loginToken: token } } = store.getState();
  // const locale = navigator.language || 'en-US';
  // Use en-US as the default language until localization is implemented
  config.headers['Accept-Language'] = 'en-US';
  config.headers.Authorization = token ? `Bearer ${token}` : undefined;
  return config;
});

export const getActiveHeroConversations = () =>
  httpClient
    .get<{ conversations: HeroConversation[] }>(`/conversations/active`)
    .then((response) => response.data.conversations);

export const getArchivedHeroConversations = (page = 0) =>
  httpClient
    .get<
      {
        conversation: HeroConversation;
        archiveAttributes: ArchivedHeroConversationAttributes;
      }[]
    >(`/conversations/v2/archive?page=${page}`)
    .then((response) => {
      const archivedConversations = response.data.map(
        (archivedConversation) => ({
          ...archivedConversation.conversation,
          archiveAttributes: archivedConversation.archiveAttributes,
        }),
      );
      return {
        conversations: archivedConversations,
        totalCount: response.headers['x-total-count'] as number,
      };
    });

export const getHeroConversationById = (conversationId: string) =>
  httpClient
    .get<HeroConversation>(`/conversations/${conversationId}`)
    .then((response) => response.data);

export const getTwilioAuthToken = () =>
  httpClient
    .get<{ twilioJwtToken: string }>('/auth/token/twilio')
    .then((response) => response.data.twilioJwtToken);

type GetRetailerProductByUrlPayload = {
  regionId?: string;
  retailerId: string;
  url: string;
};

type GetRetailerProductByUrlVariantResponse = {
  sku: string;
  price: number;
  salePrice?: number;
  currency: keyof typeof currencies;
  thumbnail: string;
  url: string;
  size?: string;
  color?: string;
};

type GetRetailerProductByUrlResponse = {
  sku: string;
  price: number;
  salePrice?: number;
  currency: keyof typeof currencies;
  thumbnail: string;
  title: string;
  url: string;
  retailer: string;
  retailerId: string;
  images?: string[];
  size?: string;
  color?: string;
  variants?: GetRetailerProductByUrlVariantResponse[];
  defaultVariantIndex?: number;
};

const extractProductData = (data: GetRetailerProductByUrlResponse): Product => {
  const variant = data.variants?.[data.defaultVariantIndex || 0] || data;

  return {
    sku: variant.sku,
    price: variant.price,
    salePrice: variant.salePrice,
    thumbnail: variant.thumbnail,
    title: data.title, // Always use parent product title
    currency: variant.currency,
    url: variant.url,
    retailer: data.retailer,
    retailerId: data.retailerId,
    images: data.images,
  };
};

export const getRetailerProductByUrl = ({
  regionId,
  retailerId,
  url,
}: GetRetailerProductByUrlPayload): Promise<Product> =>
  httpClient
    .get<GetRetailerProductByUrlResponse>('/products/v4/products-by-url', {
      headers: {
        'x-hero-merchant-id': retailerId,
      },
      params: {
        url,
        ...(regionId && { regionId }),
      },
    })
    .then(({ data: product }) => extractProductData(product))

export const getAssociateSavedReplies = (queryParams: URLSearchParams) =>
  httpClient
    .get<SavedReply[]>(`/content/v2/saved-replies?${queryParams.toString()}`, {
      params: {
        size: 500,
      },
    })
    .then((response) => response.data);

export const getRetailerStores = (retailerId: string) =>
  httpClient
    .get<Store[]>(`/retailers/${retailerId}/stores`)
    .then((response) => response.data);

export const getAssociate = () =>
  httpClient.get<Associate>('/users/v3/me').then((response) => response.data);

export const getAssociateSetupState = () =>
  httpClient
    .get<AssociateSetupResponse>('/onboarding/v2/state')
    .then((response) => response.data);

export const getAssociateIntroModules = () =>
  httpClient
    .get<{
      introModules: IntroModule[];
    }>('/onboarding/v6/intro-modules', {
      headers: { 'x-api-version': '2.0' },
      params: { device: 'desktop' },
    })
    .then((response) => response.data);

export const getAssociateChatRating = () =>
  httpClient
    .get<{ averageRating: number }>('/users/v3/rating')
    .then((response) => response.data);

// This endpoint has support for pagination, which might be needed in the future
export const getAssociateInsights = () =>
  httpClient
    .get<AssociateInsight[]>('/associates/insights')
    .then((response) => response.data);

export const getPosts = (page: number, pageSize = 6) =>
  httpClient
    .get<
      Post[]
    >('/stories', { params: { page, ...(pageSize ? { size: pageSize } : {}) } })
    .then((response) => ({
      posts: response.data,
      totalCount: response.headers['x-total-count'] as number,
    }));

export const getIntroModulesAsPosts = () =>
  httpClient
    .get<{
      introPosts: IntroModuleAsPost[];
    }>('/onboarding/v6/intro-posts', { params: { device: 'desktop' } })
    .then((response) => response.data);

export const markPostAsRead = (postId: string) =>
  httpClient
    .post<Post[]>(`stories/${postId}/read`)
    .then((response) => response.data);

export const getRetailerStoreOpeningHours = (storeId: string) =>
  httpClient
    .get<StoreOpeningHours>(`/stores/${storeId}/open`)
    .then((response) => response.data);

export const getAssociateSettings = () =>
  httpClient
    .post<AssociateSettingsBackendResponse>('/associates/settings', {
      platform: 'web',
    })
    .then((response) => response.data);

export const claimConversation = ({
  conversationId,
  associateId,
}: {
  conversationId: string;
  associateId: string;
}) =>
  httpClient.post(`/conversations/${conversationId}/claim`, {
    user: {
      id: associateId,
    },
  });

export const blockConversation = ({
  conversationId,
}: {
  conversationId: string;
}) => httpClient.put(`/conversations/${conversationId}/block`);

export const addConversationParticipant = ({
  conversationId,
  userToAdd,
}: {
  conversationId: string;
  userToAdd: string;
}) =>
  httpClient.post(
    `/conversations/${conversationId}/participants`,
    {
      userToAdd,
    },
    {
      validateStatus: (status: number) => {
        return status === 200 || status === 422;
      },
    },
  );

export const getAssociateEvents = () =>
  httpClient
    .get<VsAssociateEvent[]>(`/associate-events/recent?size=10&maxAgeHours=8`)
    .then((response) => response.data);

export const readAssociateEvent = (eventId: string) =>
  httpClient.post(`/associate-events/${eventId}/read`);

export const declineAssociateEvent = ({
  eventId,
  goUnavailable,
}: {
  eventId: string;
  goUnavailable: boolean;
}) =>
  httpClient.post(`/associate-events/${eventId}/decline`, { goUnavailable });

export const getAssociateIncomingOpenTransferRequests = () =>
  httpClient
    .get<VsTransferEvent[]>('/transfers/open/requested')
    .then((response) => response.data);

export const declineTransferRequest = (transferRequestId: string) =>
  httpClient.put(`/transfers/${transferRequestId}/decline`);

export const acceptTransferRequest = (transferRequestId: string) =>
  httpClient.put(`/transfers/${transferRequestId}/accept`);

export const getAssociatesForTransferFilters = (teams: string[]) =>
  httpClient
    .get<
      AssociateFilter[]
    >(`/transfers/users/filters`, { params: { ...(teams.length ? { filters: teams.join(',') } : {}) } })
    .then((response) => response.data);

export const getAssociatesForTransfer = (teams: string[]) =>
  httpClient
    .get<
      Associate[]
    >(`/transfers/users`, { params: { ...(teams.length ? { filters: teams.join(',') } : {}) } })
    .then((response) =>
      response.data.map((associate) => ({ ...associate, id: associate._id })),
    );

export const createAssociateTransferRequest = (payload: {
  associateIds: string[];
  conversationId: string;
  note: string;
}) =>
  httpClient.post(`/transfers/request`, {
    requested: payload.associateIds,
    conversationId: payload.conversationId,
    note: payload.note,
  });

export const cancelAssociateTransferRequest = (transferId: string) =>
  httpClient.put(`/transfers/${transferId}/cancel`);

export const getAssociateOutgoingOpenTransferRequests = (
  conversationId: string,
) =>
  httpClient
    .get<
      VsTransferEvent[]
    >(`/transfers/open/created/conversation/${conversationId}`)
    .then((response) => response.data);

export const getShopperEvents = (shopperId: string) =>
  httpClient
    .get<ShopperEvent[]>(`/shopper-events/${shopperId}`)
    .then((response) => response.data);

export const endConversation = (conversationId: string) =>
  httpClient.put(`/conversations/${conversationId}/end`);

export const requestResetCodeRequest = (payload: { email: string }) =>
  httpClient.post(`/monolith/auth/reset/token`, {
    email: payload.email,
    platform: 'web',
  });

export const validateResetTokenRequest = (payload: {
  email: string;
  token: string;
}) =>
  httpClient.post(`/monolith/auth/reset/validate`, {
    email: payload.email,
    nonceToken: payload.token,
  });

export const updatePasswordByTokenRequest = (payload: {
  email: string;
  token: string;
  password: string;
  passwordConfirm: string;
}) =>
  httpClient.post(`/monolith/auth/reset/update`, {
    email: payload.email,
    nonceToken: payload.token,
    password: payload.password,
    passwordConfirm: payload.passwordConfirm,
  });

export const getSmartResponses = () =>
  httpClient
    .get<{ smartResponses: SmartResponse[] }>('/smart-responses')
    .then((response) => response.data);

export const addContact = (customerId: string) =>
  httpClient.post('/contacts/request', { customerId });

export const getContactConversations = () =>
  httpClient
    .get<ConversationContactResponse[]>(`/contacts/v2`)
    .then((response) =>
      (response.data || []).map(({ contact }) => ({
        id: contact._id,
        firstName: contact.firstName,
        deliveryChannelType: contact.deliveryChannelType,
        conversationId: contact.conversationId,
        twilioConversationId: contact.twilioConversationId,
      })),
    );
